import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase/client';
import { useBranding } from '@/lib/branding/BrandingProvider';

interface NeuraxEmployee {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  role: 'admin' | 'member' | 'viewer' | string;
  is_active: boolean;
  b2b_company_id: string;
}

interface AuthCtx {
  loading: boolean;
  session: Session | null;
  user: User | null;
  employee: NeuraxEmployee | null;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { companyId } = useBranding();
  const [session, setSession] = useState<Session | null>(null);
  const [employee, setEmployee] = useState<NeuraxEmployee | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session ?? null);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadEmployee() {
      if (!session?.user) {
        setEmployee(null);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('b2b_employees')
        .select('id, user_id, email, full_name, role, is_active, b2b_company_id')
        .eq('user_id', session.user.id)
        .eq('b2b_company_id', companyId)
        .eq('is_active', true)
        .maybeSingle();

      if (cancelled) return;

      if (error || !data) {
        setEmployee(null);
        await supabase.auth.signOut();
      } else {
        setEmployee(data as NeuraxEmployee);
      }
      setLoading(false);
    }

    setLoading(true);
    loadEmployee();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id, companyId]);

  async function signIn(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  }

  async function signOut() {
    await supabase.auth.signOut();
    setEmployee(null);
  }

  const value: AuthCtx = {
    loading,
    session,
    user: session?.user ?? null,
    employee,
    isAdmin: employee?.role === 'admin',
    signIn,
    signOut,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
